import React from 'react'
import PropTypes from 'prop-types'

import { Button, Input, Group, useForm, useFela, theme } from '@fortressiq/fiq-ds'

import api from 'lib/Api'
import { saveAuditLogs } from 'helpers'
import ControlledSelect from 'components/form/ControlledSelect'
import FieldWrapper from 'components/form/FieldWrapper'

import ControlledDomainInput from './ControlledDomainInput'

import { modalWrapperCSS } from './styles'

const accessTypeOptions = [
  { label: 'Allow', value: 'allow' },
  { label: 'Block', value: 'block' },
]

const generateDefaultValues = editValues => {
  // Remove none form fields: categoryType, createdAt, createdBy, id, updatedAt, observers,
  const { categoryType, createdAt, createdBy, id, updatedAt, observers, accessType, list, ...formFields } = editValues
  return {
    ...formFields,
    // get value label obj for select inputs, based on value from options array
    domains: list,
    accessType: accessTypeOptions.filter(option => option.value === accessType)[0],
  }
}

const DomainListForm = ({ editId, editValues, handleReFetch, removeModal, isInUse }) => {
  const isEditing = !!editId

  const { css } = useFela()

  const { register, clearErrors, setError, handleSubmit, control, formState } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: editValues ? { ...generateDefaultValues(editValues) } : { domains: [] },
  })

  const onSubmitHandler = async formValues => {
    const params = {
      list: formValues.domains,
      name: formValues.name,
      accessType: formValues.accessType.value,
      categoryType: 'url',
    }

    // If editId exist, we are in edit mode and use api.put instead of post
    try {
      const data = isEditing ? api.put(`/allow_block_lists/${editId}`, params) : api.post('/allow_block_lists', params)
      await data

      let newValues = params
      const previousValues = {}
      if (isEditing) {
        newValues = {}
        Object.keys(params).forEach(key => {
          if (params[key] !== editValues[key]) {
            newValues[key] = params[key]
            previousValues[key] = editValues[key]
          }
        })
      }

      saveAuditLogs({
        action: isEditing ? 'update' : 'create',
        newValues,
        previousValues: isEditing ? previousValues : null,
        configType: 'Domain List Configuration',
      })

      handleReFetch()
      removeModal()
    } catch (err) {
      if (err.response.data.errors.includes('Name already exists for this tenant')) {
        setError('name', {
          type: 'manual',
          message: `Name "${formValues.name}" already exist`,
        })
      }
    }
  }

  return (
    <div className={css(modalWrapperCSS)}>
      <form onSubmit={handleSubmit(onSubmitHandler)}>
        <FieldWrapper htmlFor='name' error={formState.errors.name}>
          <Input
            {...register('name', { required: 'This is required.' })}
            label='Name'
            isRequired={true}
            placeholder='Name of Domain List.'
          />
        </FieldWrapper>
        <FieldWrapper htmlFor='accessType' isRequired={true} label='Type' error={formState.errors.accessType}>
          <ControlledSelect
            control={control}
            isDisabled={isInUse}
            isMulti={false}
            name='accessType'
            options={accessTypeOptions}
            placeholder='Select Type of Domain List.'
            portal={true}
            rules={{ required: 'This field is required' }}
          />
        </FieldWrapper>
        <ControlledDomainInput
          clearErrors={clearErrors}
          control={control}
          error={formState.errors.domains}
          isRequired={true}
          name='domains'
          placeholder='Domain to be added to Domain List.'
          setError={setError}
        />
        <Group noEndGutter={true} justify='flex-end' style={{ marginTop: theme['default-spacer-md'] }}>
          <Button
            onClick={e => {
              e.preventDefault()
              removeModal()
            }}
          >
            Cancel
          </Button>
          <Button type='primary'>{isEditing ? 'Update List' : 'Create List'}</Button>
        </Group>
      </form>
    </div>
  )
}

DomainListForm.defaultProps = {
  editValues: undefined,
  editId: undefined,
  isInUse: false,
}

DomainListForm.propTypes = {
  editId: PropTypes.number,
  editValues: PropTypes.shape({
    list: PropTypes.arrayOf(PropTypes.string),
    name: PropTypes.string,
    accessType: PropTypes.string,
  }),
  handleReFetch: PropTypes.func.isRequired,
  removeModal: PropTypes.func.isRequired,
  isInUse: PropTypes.bool,
}

export default DomainListForm
